import { Component, computed, inject, OnInit, signal, ChangeDetectionStrategy } from "@angular/core";
import { Marketplace, MatchType, Targeting } from "@front/m19-api-client";
import { SegmentConfigType, SegmentEx } from "@front/m19-models";
import { SegmentService } from "@front/m19-services";
import {
  IBadgeComponent,
  IButtonDirective,
  IIconComponent,
  ISelectMenuImports,
  ISwitchButtonComponent,
  MODAL_DATA,
  ModalRef,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { ProductViewComponent } from "@m19-board/product-view/product-view.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

const CREATE_NEW_SEGMENT = "create_new_segment" as const;

@UntilDestroy()
@Component({
  selector: "app-add-to-segment-modal",
  templateUrl: "./add-to-segment-modal.component.html",
  imports: [
    ISelectMenuImports,
    IBadgeComponent,
    ProductViewComponent,
    TranslocoDirective,
    IButtonDirective,
    ISwitchButtonComponent,
    IIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddToSegmentModalComponent implements OnInit {
  private segmentService = inject(SegmentService);
  private toasterService = inject(ToasterService);

  protected readonly modalData = inject(MODAL_DATA) as AddToSegmentModalData;
  protected readonly modalRef = inject(ModalRef);

  protected readonly CREATE_NEW_SEGMENT = CREATE_NEW_SEGMENT;

  matchType = MatchType.exact;
  segments = signal<SegmentEx[]>([]);
  readonly SegmentType = SegmentConfigType;
  readonly MatchType = MatchType;

  selectedSegment = signal<SegmentEx | typeof CREATE_NEW_SEGMENT>(CREATE_NEW_SEGMENT);
  searchQuery = signal("");

  filteredSegments = computed(() => {
    const segments = this.segments().filter((s) => s.segmentType == this.modalData.segmentType && s.name);
    const query = this.searchQuery().toLowerCase();
    if (!query) return segments;
    return segments.filter((s) => s.name!.toLowerCase().includes(query));
  });

  targetingItemValues: string[] = [];

  constructor() {
    if (this.modalData.targetingItemValue) {
      this.targetingItemValues = [this.modalData.targetingItemValue];
    }
  }

  ngOnInit(): void {
    this.segmentService
      .getSegments(this.modalData.accountId, this.modalData.marketplace)
      .pipe(untilDestroyed(this))
      .subscribe((segmentIndex) => {
        this.segments.set(Array.from(segmentIndex.values()));
        this.selectedSegment.set(CREATE_NEW_SEGMENT);
      });
  }

  selectSegment(segment: SegmentEx | typeof CREATE_NEW_SEGMENT | undefined) {
    if (segment == null) return;
    this.selectedSegment.set(segment);
  }

  close(): void {
    this.modalRef.close();
  }

  setMatchType(matchType: MatchType) {
    this.matchType = matchType;
  }

  save(): void {
    const selected = this.selectedSegment();
    if (selected !== CREATE_NEW_SEGMENT) {
      if (this.modalData.segmentType == SegmentConfigType.KeywordSegment) {
        this.segmentService
          .addKeywordsToSegment(
            this.modalData.accountId,
            this.modalData.marketplace,
            selected.segmentId,
            this.targetingItemValues.map((v) => ({
              targetingValue: v,
              matchType: this.matchType,
            })),
          )
          .pipe(untilDestroyed(this))
          .subscribe({
            next: () => {
              this.toasterService.success(
                `Keyword${this.targetingItemValues.length > 1 ? "s" : ""} ${this.targetingItemValues.slice(0, 3)}${
                  this.targetingItemValues.length > 3 ? "…" : ""
                } successfully added to segment`,
                "Keyword(s) added to segment",
              );
              this.modalRef.close();
            },
            error: (error) => {
              this.toasterService.error(error, "Keyword segment update error");
            },
          });
      } else {
        this.segmentService
          .addAsinsToSegment(
            this.modalData.accountId,
            this.modalData.marketplace,
            selected.segmentId,
            this.targetingItemValues,
          )
          .pipe(untilDestroyed(this))
          .subscribe({
            next: () => {
              this.toasterService.success(
                `ASIN${this.targetingItemValues.length > 1 ? "s" : ""} ${this.targetingItemValues.slice(0, 3)}${
                  this.targetingItemValues.length > 3 ? "…" : ""
                } successfully added to segment ${selected.name}`,
                "ASIN(s) added to segment",
              );
              this.modalRef.close();
            },
            error: (error) => {
              this.toasterService.error(error, "Product segment update error");
            },
          });
      }
    } else {
      let segmentItems: Targeting[];
      if (this.modalData.segmentType == SegmentConfigType.KeywordSegment) {
        segmentItems = this.targetingItemValues.map((v) => ({ matchType: this.matchType, targetingValue: v }));
      } else {
        segmentItems = this.targetingItemValues.map((v) => ({ matchType: MatchType.asinSameAs, targetingValue: v }));
      }
      this.modalRef.close(segmentItems);
    }
  }
}

export interface AddToSegmentModalData {
  segmentType: SegmentConfigType;
  targetingItemValue: string;
  marketplace: Marketplace;
  accountId: string;
}
